import React, { useEffect, useState } from "react";
import { Globe, Loader2, Copy, ExternalLink, RefreshCw, CheckCircle2, AlertTriangle, Trash2, Image as ImageIcon, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fmtDateTime } from "../../lib/ist";

export default function MySites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dnsStates, setDnsStates] = useState({});
  const [checking, setChecking] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sites/me"); setSites(data); }
    catch { toast.error("Failed to load sites"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const verifyDns = async (slug) => {
    setChecking(slug);
    try {
      const { data } = await api.get(`/sites/${slug}/dns-check`);
      setDnsStates(s => ({ ...s, [slug]: data }));
      if (data.configured) toast.success("DNS is configured correctly!");
      else toast.message("DNS not pointing to Optivize yet");
    } catch (e) { toast.error(e?.response?.data?.detail || "DNS check failed"); }
    finally { setChecking(null); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete published site '${slug}'?`)) return;
    try { await api.delete(`/sites/${slug}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="space-y-5" data-testid="my-sites-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <Globe size={26} className="text-blue-400" /> My Published Sites
          </h1>
          <p className="text-slate-400 text-sm">Manage subdomains, custom domains and verify DNS configuration.</p>
        </div>
        <button onClick={load} className="btn-ghost !py-2 !px-3"><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : sites.length === 0 ? (
        <div className="card p-10 text-center">
          <Globe size={32} className="text-blue-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-white font-semibold">No published sites yet</h3>
          <p className="text-slate-400 text-sm mt-1">Generate a website from the AI Builder and publish to your subdomain or custom domain.</p>
          <a href="/ai-builder" className="btn-primary mt-4 inline-flex items-center gap-2">Open AI Builder</a>
        </div>
      ) : (
        <div className="space-y-4">
          {sites.map(s => {
            const dns = dnsStates[s.slug];
            const liveUrl = s.destination === "custom_domain" && s.custom_domain
              ? `https://${s.custom_domain}`
              : `https://${s.slug}.optivize.digital`;
            const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sites/${s.slug}/html`;
            return (
              <div key={s.slug} className="card p-5 space-y-3" data-testid={`site-${s.slug}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold text-lg truncate">{s.slug}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.published ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>{s.published ? "Live" : "Draft"}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">{s.destination || "subdomain"}</span>
                      {typeof s.seo_score === "number" && (
                        <Link to="/dashboard/seo/analyzer"
                              title="View full SEO breakdown"
                              data-testid={`seo-score-${s.slug}`}
                              className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors hover:opacity-80 ${
                                s.seo_score >= 80 ? "bg-emerald-500/20 text-emerald-300" :
                                s.seo_score >= 55 ? "bg-amber-500/20 text-amber-300" :
                                                    "bg-rose-500/20 text-rose-300"
                              }`}>
                          <Search size={10}/> SEO {s.seo_score}
                        </Link>
                      )}
                    </div>
                    <div className="text-slate-400 text-xs mt-1">Created {fmtDateTime(s.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <a href={previewUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`preview-${s.slug}`}><ExternalLink size={13} /> Preview</a>
                    <button onClick={() => remove(s.slug)} className="text-rose-300 hover:text-rose-200 p-1.5" title="Delete" data-testid={`delete-${s.slug}`}><Trash2 size={15} /></button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Live URL</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <code className="text-cyan-300 text-sm truncate flex-1">{liveUrl}</code>
                      <button onClick={() => copy(liveUrl)} className="text-slate-400 hover:text-white p-1" data-testid={`copy-url-${s.slug}`}><Copy size={13} /></button>
                    </div>
                  </div>
                  {s.og_image_url && (
                    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><ImageIcon size={11} /> OG share card</div>
                      <a href={s.og_image_url} target="_blank" rel="noreferrer" className="text-cyan-300 text-xs hover:underline truncate block mt-1">{s.og_image_url}</a>
                    </div>
                  )}
                </div>

                {s.destination === "custom_domain" && (
                  <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl p-4 space-y-3" data-testid={`dns-card-${s.slug}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Globe size={14} className="text-blue-400" /> DNS Configuration for {s.custom_domain}
                      </h4>
                      <button onClick={() => verifyDns(s.slug)} disabled={checking === s.slug}
                        className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                        data-testid={`verify-dns-${s.slug}`}
                      >
                        {checking === s.slug ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Verify DNS
                      </button>
                    </div>

                    {dns && (
                      <div className={`text-sm flex items-center gap-2 ${dns.configured ? "text-emerald-300" : "text-amber-300"}`} data-testid={`dns-result-${s.slug}`}>
                        {dns.configured ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                        {dns.configured
                          ? `Configured · ${dns.host} → ${dns.current_ip}`
                          : `Not yet pointing here. Current IP: ${dns.current_ip || "—"} · Expected: ${dns.expected_ip || "—"}`}
                      </div>
                    )}

                    <p className="text-slate-400 text-xs">Add ONE of the following records at your domain registrar:</p>
                    <div className="space-y-1.5 text-xs">
                      <DnsRow type="CNAME" host="@ (or www)" value="optivize.digital" onCopy={copy} />
                      {dns?.expected_ip && <DnsRow type="A" host="@" value={dns.expected_ip} onCopy={copy} />}
                    </div>
                    <p className="text-slate-500 text-[11px]">After saving the record, click Verify DNS. Propagation can take a few minutes up to 48 hours depending on your registrar.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DnsRow({ type, host, value, onCopy }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 font-mono text-xs">
      <span className="col-span-2 text-blue-300">{type}</span>
      <span className="col-span-3 text-slate-300">{host}</span>
      <span className="col-span-6 text-cyan-300 truncate">{value}</span>
      <button onClick={() => onCopy(value)} className="col-span-1 text-slate-400 hover:text-white flex items-center justify-end"><Copy size={12} /></button>
    </div>
  );
}
